import { useEffect, useMemo } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useAppDispatch } from '../../store';
import {
    fetchAIPurchaseBrands,
    fetchAIPurchaseStores,
    useAIPurchaseBrands,
    useAIPurchaseStores,
    useAIPurchaseLoading,
    useAIPurchaseError,
} from '../../store/slices/aiPurchaseAnalyticsSlice';
import { useAIPurchaseDateRangeParams } from './hooks/useAIPurchaseDateRangeParams';
import { AI_PURCHASE_BRAND_HEADER_LOOKUP_LIMIT } from '../../constants/api.constants';
import AIPurchaseDateRangeControls from './components/AIPurchaseDateRangeControls';
import AIPurchaseBreadcrumbs from './components/AIPurchaseBreadcrumbs';
import OverallSummaryCard from './components/OverallSummaryCard';
import StoreDrilldownTable from './components/StoreDrilldownTable';

interface NavigationState {
    brandName?: string;
}

/**
 * Page 3 — per-store AI-linked vs non-AI-linked breakdown within one brand/state.
 * Shows the cumulative totals/percentages for this specific brand at the top
 * (found within the state's brand-breakdown list — no extra endpoint needed, per
 * the rework notes), then the store-level table below.
 */
export default function AIPurchaseNumbersBrandPage() {
    const dispatch = useAppDispatch();
    const location = useLocation();
    const { state: encodedState, brandId } = useParams<{ state: string; brandId: string }>();
    const state = decodeURIComponent(encodedState ?? '');

    const { fromDate, toDate, queryString, setDateRange } = useAIPurchaseDateRangeParams();

    const brands = useAIPurchaseBrands();
    const stores = useAIPurchaseStores();
    const loading = useAIPurchaseLoading();
    const error = useAIPurchaseError();

    useEffect(() => {
        if (!state || !brandId) return;
        // Fetches the full (unpaginated, unfiltered) brand list for this state just to
        // look up this one brand's cumulative totals for the header below — the
        // brand-breakdown grid itself (rendered on the state page, not here) owns its
        // own separately-paginated/searched fetches against the same endpoint.
        dispatch(
            fetchAIPurchaseBrands({
                state,
                fromDate,
                toDate,
                limit: AI_PURCHASE_BRAND_HEADER_LOOKUP_LIMIT,
            })
        );
        dispatch(fetchAIPurchaseStores({ state, brandId, fromDate, toDate }));
    }, [dispatch, state, brandId, fromDate, toDate]);

    const brandSummary = useMemo(
        () => brands.find((item) => item.brandId === brandId) ?? null,
        [brands, brandId]
    );

    // Optimistic label from navigation state (set when clicking a brand card) so the
    // breadcrumb/heading don't flash a placeholder while the brand list refetches —
    // falls back gracefully on a direct URL visit/refresh where there is no nav state.
    const navigationState = location.state as NavigationState | null;
    const brandName = brandSummary?.brandName ?? navigationState?.brandName ?? 'Brand';

    return (
        <div className="w-full space-y-6">
            <AIPurchaseBreadcrumbs
                items={[
                    { label: 'AI Purchase Adoption', to: `/ai-purchase-numbers?${queryString}` },
                    {
                        label: state,
                        to: `/ai-purchase-numbers/state/${encodeURIComponent(state)}?${queryString}`,
                    },
                    { label: brandName },
                ]}
            />

            <div className="flex flex-col md:flex-row items-start md:items-end justify-between gap-4">
                <div>
                    <h2 className="text-4xl font-bold text-[#1e293b] leading-tight">{brandName}</h2>
                    <p className="text-lg text-[#64748b] mt-2">
                        AI purchase adoption by store in {state} for the selected date range.
                    </p>
                </div>

                <AIPurchaseDateRangeControls
                    fromDate={fromDate}
                    toDate={toDate}
                    onApply={setDateRange}
                />
            </div>

            <OverallSummaryCard
                title={`${brandName} — Overall`}
                description={`Cumulative AI purchase adoption across every store for this brand in ${state}.`}
                overall={brandSummary}
                loading={loading.brands === 'loading'}
                error={error.brands}
            />

            <div>
                <h3 className="text-lg font-semibold text-[#1e293b] mb-3">By store</h3>
                <StoreDrilldownTable
                    state={state}
                    brandName={brandName !== 'Brand' ? brandName : null}
                    stores={stores}
                    loading={loading.stores === 'loading'}
                    error={error.stores}
                />
            </div>
        </div>
    );
}
